import React, { useContext, useState } from 'react';
import {
  Box,
  Text,
  Flex,
  Radio,
  RadioGroup,
  Stack,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { collabPreferenceOptions } from '../../../types';
import { influencerApi } from 'services/api/influencer';
import { InfluencerDataContext } from 'contexts/InfluencerDataContext';
import { ff } from '../../../theme';

interface CollaborationPreferencesEditProps {
  collabPreference: string;
  setCollabPreference: (preference: string) => void;
  associatedTags: string[];
  lastSavedCollabPreferences: {
    collabPreference: string;
  } | null;
  onCollabPreferencesSaved: (preference: string) => void;
  onBack: () => void;
}

const CollaborationPreferencesEdit: React.FC<CollaborationPreferencesEditProps> = ({
  collabPreference,
  setCollabPreference,
  associatedTags,
  lastSavedCollabPreferences,
  onCollabPreferencesSaved,
  onBack,
}) => {
  const toast = useToast();
  const { influencerData } = useContext(InfluencerDataContext);
  const [savingPreference, setSavingPreference] = useState<string | null>(null);

  /**
   * Autosave collaboration preference
   */
  const autosavePreference = async (newPreference: string) => {
    if (!lastSavedCollabPreferences || !influencerData?.id) return;
    if (newPreference === lastSavedCollabPreferences.collabPreference) return;
    
    try {
      setSavingPreference(newPreference);
      
      await influencerApi.updateProfile({
        collabPreference: newPreference,
        associatedTags
      }, influencerData.id);
      
      // Update last saved value on success
      onCollabPreferencesSaved(newPreference);
    } catch (error) {
      console.error('Error autosaving collaboration preference:', error);
      // Revert to last saved value
      setCollabPreference(lastSavedCollabPreferences.collabPreference);
      toast({
        title: "Couldn't save collaboration preference",
        description: "Reverted to last saved value",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSavingPreference(null);
    }
  };

  const handlePreferenceChange = (newPreference: string) => {
    setCollabPreference(newPreference);
    autosavePreference(newPreference);
  };

  return (
    <Box>
      {/* Description */}
      <Text color={ff.text2} fontSize="14px" mb={5}>
        Select your preferred collaboration type
      </Text>

      {/* Radio Options */}
      <RadioGroup value={collabPreference} onChange={handlePreferenceChange}>
        <Stack spacing={3}>
          {collabPreferenceOptions.map((option) => {
            const isSaving = savingPreference === option.value;
            const isSelected = collabPreference === option.value;

            return (
              <Box
                key={option.value}
                position="relative"
                border="1px solid"
                borderColor={isSelected ? ff.ink : ff.line}
                borderRadius="20px"
                px={4}
                minH="56px"
                display="flex"
                alignItems="center"
                bg={isSelected ? ff.limeSurface : 'white'}
                cursor={isSaving ? 'not-allowed' : 'pointer'}
                _hover={{ borderColor: ff.ink }}
                transition="all 0.2s"
                onClick={() => !isSaving && handlePreferenceChange(option.value)}
              >
                {/* Loading Overlay */}
                {isSaving && (
                  <Flex
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bg="rgba(255, 255, 255, 0.8)"
                    borderRadius="20px"
                    align="center"
                    justify="center"
                    zIndex={2}
                  >
                    <Spinner size="md" color={ff.ink} thickness="3px" />
                  </Flex>
                )}
                
                <Flex alignItems="center" justifyContent="space-between" w="100%">
                  <Text fontWeight={600} fontSize="15px" color={ff.ink}>{option.label}</Text>
                  <Radio
                    value={option.value}
                    sx={{
                      '.chakra-radio__control': { borderColor: ff.border },
                      '.chakra-radio__control[data-checked]': { bg: ff.ink, borderColor: ff.ink, color: ff.lime },
                    }}
                  />
                </Flex>
              </Box>
            );
          })}
        </Stack>
      </RadioGroup>
    </Box>
  );
};

export default CollaborationPreferencesEdit;